import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Image,
  Button,
  Heading,
  VStack,
  Container,
  Text,
  Flex,
  useBreakpointValue,
  HStack,
} from "@chakra-ui/react";

const PAPER = "#F6F1E4";
const INK = "#16233A";
const NAVY = "#0E1A2B";
const AF_BLUE = "#002157";
const AF_BLUE_HOVER = "#013580";

const ORIGIN = "BUH";
const BOOK_URL = "https://tbs.flytime.ro/reseller/auth/";
const IMG_BASE = "https://travel-time.ro/images/af-kl/af-mar-2026";

const BANNER_IMAGE = `/images/af-kl/aifrance-header-sep.jpg`;

// TODO: înlocuiește prețurile cu cele reale
const destinations = [
  {
    code: "MIA",
    city: "Miami",
    region: "Florida, SUA",
    price: "679",
    image: `https://travel-time.ro/images/af-kl/af-sep-2025/Miami.jpg`,
  },
  {
    code: "PTP",
    city: "Pointe-à-Pitre",
    region: "Guadelupa",
    price: "856",
    image: `https://travel-time.ro/images/af-kl/af-sep-2025/guadeloupe.jpg`,
  },
  {
    code: "LIM",
    city: "Lima",
    region: "Peru",
    price: "1009",
    image: `https://travel-time.ro/images/af-kl/kl-mar-2026/lima.jpg`,
  },
  {
    code: "PUJ",
    city: "Punta Cana",
    region: "Rep. Dominicană",
    price: "767",
    image: `https://travel-time.ro/images/af-kl/af-sep-2025/punta-cana.jpg`,
  },
];

export const GridPromoAfSEP26 = () => {
  const [mounted, setMounted] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  // 1 coloană pe mobil, 2 pe tablete, max 2 pe ecrane mari -> carduri mai late
  const columns = useBreakpointValue({ base: 1, sm: 2, lg: 4 });
  const bannerH = useBreakpointValue({ base: "260px", md: "380px" });
  // panoul foto e acum mult mai înalt, ca imaginile să se vadă complet
  const photoH = useBreakpointValue({
    base: "260px",
    sm: "300px",
    lg: "340px",
  });

  return (
    <Box bg={NAVY} minH="100vh" position="relative" overflow="hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');
      `}</style>

      {/* Hero banner */}
      <Box position="relative" h={bannerH} overflow="hidden">
        <Image
          src={BANNER_IMAGE}
          alt="Zboruri Air France din București"
          w="full"
          h="full"
          objectFit="cover"
        />
        <Box
          position="absolute"
          inset={0}
          bgGradient={`linear(to-t, ${NAVY} 5%, rgba(14,26,43,0.55) 35%, rgba(14,26,43,0.15) 50%)`}
        />
        <Container
          maxW="7xl"
          position="absolute"
          inset={0}
          px={{ base: 5, md: 8 }}
        >
          <Flex
            direction="column"
            justify="flex-end"
            h="full"
            pb={{ base: 6, md: 8 }}
            maxW="lg"
          >
            <Heading
              fontFamily="'Fraunces', serif"
              fontWeight="500"
              fontSize={{ base: "3xl", md: "5xl" }}
              color={PAPER}
              lineHeight="1.05"
            >
              Din București, drept spre emisfera vestică
            </Heading>
          </Flex>
        </Container>
      </Box>

      {/* faint dotted route-map texture over the rest of the page */}
      <Box
        position="absolute"
        top={bannerH}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        backgroundImage="radial-gradient(circle, #ffffff 1px, transparent 1px)"
        backgroundSize="28px 28px"
        pointerEvents="none"
      />

      <Container
        maxW="7xl"
        position="relative"
        py={{ base: 10, md: 14 }}
        px={{ base: 5, md: 8 }}
      >
        <HStack
          spacing={3}
          border="1px solid"
          borderColor="whiteAlpha.300"
          rounded="full"
          px={4}
          py={2}
          w="fit-content"
          mb={{ base: 8, md: 10 }}
        >
          <Box
            w="6px"
            h="6px"
            rounded="full"
            bg={AF_BLUE}
            border="1px solid"
            borderColor="whiteAlpha.600"
          />
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="sm"
            color="whiteAlpha.800"
          >
            Clasa Economy · plecare din {ORIGIN}
          </Text>
        </HStack>

        {/* Ticket grid */}
        <Grid
          templateColumns={`repeat(${columns}, 1fr)`}
          gap={{ base: 6, md: 8 }}
        >
          {destinations.map((d, i) => {
            const isHovered = hovered === i;
            return (
              <Box
                key={d.code}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                opacity={mounted ? 1 : 0}
                transform={mounted ? "translateY(0)" : "translateY(18px)"}
                transition={`opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s`}
              >
                <Box
                  position="relative"
                  bg={PAPER}
                  rounded="lg"
                  overflow="hidden"
                  shadow={
                    isHovered
                      ? "0 20px 40px rgba(0,0,0,0.35)"
                      : "0 8px 20px rgba(0,0,0,0.2)"
                  }
                  transform={isHovered ? "translateY(-4px)" : "translateY(0)"}
                  transition="all 0.3s ease"
                >
                  {/* Photo panel */}
                  <Box position="relative" h={photoH} overflow="hidden">
                    <Image
                      src={d.image}
                      alt={d.city}
                      w="full"
                      h="full"
                      objectFit="cover"
                      objectPosition="center"
                      transition="transform 0.5s ease"
                    />
                    <Box
                      position="absolute"
                      inset={0}
                      bgGradient="linear(to-t, blackAlpha.800 15%, blackAlpha.100 60%)"
                    />

                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      px={4}
                      pt={3}
                    >
                      <HStack justify="space-between">
                        {/* <Text
                          fontFamily="'Inter', sans-serif"
                          fontSize="xs"
                          color="whiteAlpha.700"
                          letterSpacing="0.08em"
                        >
                          ZBOR DIRECT
                        </Text> */}
                        <Text
                          fontFamily="'Inter', sans-serif"
                          fontSize="xs"
                          color="whiteAlpha.700"
                          letterSpacing="0.08em"
                        >
                          ECONOMY
                        </Text>
                      </HStack>
                    </Box>

                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      px={4}
                      pb={3}
                    >
                      <Flex align="center" position="relative" h="20px">
                        <Text
                          fontFamily="'Inter', sans-serif"
                          fontWeight="700"
                          fontSize="md"
                          color={PAPER}
                        >
                          {ORIGIN}
                        </Text>
                        <Box
                          flex="1"
                          mx={3}
                          position="relative"
                          h="1px"
                          bg="whiteAlpha.500"
                        >
                          <Box
                            position="absolute"
                            top="-7px"
                            fontSize="14px"
                            left={isHovered ? "88%" : "6%"}
                            opacity={isHovered ? 1 : 0.7}
                            transition="left 1.1s ease, opacity 0.3s ease"
                          >
                            ✈
                          </Box>
                        </Box>
                        <Text
                          fontFamily="'Inter', sans-serif"
                          fontWeight="700"
                          fontSize="md"
                          color={PAPER}
                        >
                          {d.code}
                        </Text>
                      </Flex>
                    </Box>
                  </Box>

                  {/* Perforation */}
                  <Box position="relative" h="0">
                    <Box
                      position="absolute"
                      left="-10px"
                      top="-10px"
                      w="20px"
                      h="20px"
                      bg={NAVY}
                      rounded="full"
                    />
                    <Box
                      position="absolute"
                      right="-10px"
                      top="-10px"
                      w="20px"
                      h="20px"
                      bg={NAVY}
                      rounded="full"
                    />
                    <Box
                      position="absolute"
                      left="24px"
                      right="24px"
                      top="-1px"
                      borderTop="1.5px dashed"
                      borderColor="blackAlpha.300"
                    />
                  </Box>

                  {/* City info */}
                  <Flex
                    align="flex-start"
                    justify="space-between"
                    px={6}
                    pt={6}
                    pb={5}
                  >
                    <VStack align="flex-start" spacing={1}>
                      <Heading
                        fontFamily="'Fraunces', serif"
                        fontWeight="500"
                        fontSize="3xl"
                        color={INK}
                      >
                        {d.city}
                      </Heading>
                      <Text
                        fontFamily="'Inter', sans-serif"
                        fontSize="sm"
                        color="blackAlpha.600"
                      >
                        {d.region}
                      </Text>
                    </VStack>
                    <VStack align="flex-end" spacing={0} flexShrink={0} pl={2}>
                      <Text
                        fontFamily="'Inter', sans-serif"
                        fontSize="xs"
                        color="blackAlpha.500"
                      >
                        de la
                      </Text>
                      <Text
                        fontFamily="'Fraunces', serif"
                        fontWeight="600"
                        fontSize="2xl"
                        color={AF_BLUE}
                      >
                        {d.price} €
                      </Text>
                    </VStack>
                  </Flex>

                  {/* Barcode + CTA */}
                  <Flex
                    align="center"
                    justify="space-between"
                    px={6}
                    pb={6}
                    pt={2}
                  >
                    <Button
                      as="a"
                      href={BOOK_URL}
                      size="sm"
                      bg={AF_BLUE}
                      color="white"
                      rounded="full"
                      px={5}
                      fontFamily="'Inter', sans-serif"
                      fontWeight="600"
                      _hover={{ bg: AF_BLUE_HOVER }}
                    >
                      Rezervă
                    </Button>
                  </Flex>
                </Box>
              </Box>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default GridPromoAfSEP26;
